// Blog registry + helpers. To publish a new post: create a file like
// hse-ehs-eller-she.ts (copy _TEMPLATE.ts), then import and add it here.

import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { type BlogContent, type BlogPost } from "./types";
import { hseEhsEllerShe } from "./hse-ehs-eller-she";
import { miSupportKalender } from "./mi-support-kalender";

export const posts: BlogPost[] = [
  hseEhsEllerShe,
  miSupportKalender,
  // 👇 add new posts here
];

/** Full localized path to a post, e.g. /artikler/<slug> or /en/articles/<slug>. */
export function articlePath(post: BlogPost, locale: Locale): string {
  return `${pathFor("artikler", locale)}/${post.slug[locale]}`;
}

/** Whether the post has been written in the given locale. */
export function hasContent(post: BlogPost, locale: Locale): boolean {
  return post.content[locale] != null;
}

/** Posts available in a locale, newest first. */
export function postsForLocale(locale: Locale): BlogPost[] {
  return posts
    .filter((p) => hasContent(p, locale))
    .sort((a, b) => (a.datePublished < b.datePublished ? 1 : -1));
}

/** Find a post by its localized slug (only if it exists in that locale). */
export function findPostBySlug(slug: string, locale: Locale): BlogPost | undefined {
  return posts.find((p) => p.slug[locale] === slug && hasContent(p, locale));
}

/** Reverse lookup by full pathname — used by the language switcher. */
export function findPostByPath(pathname: string): { post: BlogPost; locale: Locale } | undefined {
  for (const p of posts) {
    for (const loc of ["da", "en", "de"] as const) {
      if (hasContent(p, loc) && articlePath(p, loc) === pathname) return { post: p, locale: loc };
    }
  }
  return undefined;
}

/** Estimated reading time in minutes from the body + FAQ text (~200 wpm). */
export function readingMinutes(content: BlogContent): number {
  const parts: string[] = [content.title, content.description];
  for (const b of content.body) {
    if (b.type === "paragraph" || b.type === "heading" || b.type === "quote") parts.push(b.text);
    if (b.type === "list") parts.push(...b.items);
  }
  for (const f of content.faq ?? []) parts.push(f.q, f.a);
  const words = parts.join(" ").trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}
